<template>
  <div
    :class="['conversation-timeline bg-white p-2', {'timeline-focused': timelineHovered}]"
    @mouseover="timelineHovered = true"
    @click="timelineHovered = true"
    @mouseleave="timelineHovered = false"
  >
    <h4 class="pb-3 m-0 bg-white">
      Timeline
    </h4>
    <div ref="list" class="list-group overflow-auto mb-0 pb-3">
      <ul class="m-0 p-0">
        <li v-if="conversationsLoaded && store.conversations.length === 0">
          This participant has no conversations.
        </li>
        <li
          v-for="(conversation) of store.conversations"
          :key="conversation.id"
          :class="['conversation-card card p-2 mb-2 mt-1', isActive(conversation)? 'conversation-active' : 'bg-light', {'selected-item': isSelected(conversation.id)}]"
        >
          <div :ref="`conversation-${conversation.id}`" class="conversation-title">
            <p class="my-0">
              {{ formatDate(conversation.started_at) }}<i class="conversation-icon fas fa-comment position-absolute" />
            </p>
            <p class="hide-when-narrow mb-2">
              <strong>{{ conversation.source_name }}</strong>
            </p>
            <p class="hide-when-narrow my-0">
              <i>{{ conversation.status }}</i> {{ formatStatus(conversation) }}
            </p>
            <IncidentPill
              v-for="(incident) of (conversation.incidents)"
              :key="incident.id"
              class="hide-when-narrow d-inline-block text-truncate mw-100 mt-2 mr-1"
              v-bind="incident"
            />
          </div>
        </li>
      </ul>
      <button
        v-if="lastConversation && lastConversation.status === 'Active'"
        class="btn btn-sm btn-danger text-center mw-100"
        aria-label="Stop Conversation"
        @click="showStopConversationModal = true"
      >
        <span class="align-middle d-inline-block text-truncate mw-100">
          <i class="fas fa-comment-slash text-center" />
          <span class="remove-when-narrow  m-0">Stop Conversation</span>
        </span>
      </button>
    </div>
    <W2HModal
      :show="showStopConversationModal"
      title="Stop Conversation"
      okText="Stop"
      okClass="btn btn-danger"
      :closeWhenOK="true"
      @ok="stopConversation"
      @update:show="showStopConversationModal = $event"
    >
      Are you sure you want to stop the conversation {{ lastConversation ? lastConversation.source_name : '' }}?
    </W2HModal>
  </div>
</template>

<script>
import {format, parseISO} from 'date-fns';
import IncidentPill from "@ui/IncidentPill.vue";
import W2HModal from "@ui/W2HModal.vue";

export default {
  name: "ConversationTimeline",
  components: {W2HModal, IncidentPill},
  props: {
    store:{
      type: Object,
      required: true,
    },
    selectedConversationId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      jumpedOnce: false,
      timelineHovered: false,
      conversationsLoaded: false,
      showStopConversationModal: false,
    }
  },
  computed:{
    lastConversation(){
      return this.store.conversations.slice(-1)[0];
    },
  },
  mounted() {
    this.loadConversations();
  },
  methods: {
    loadConversations() {
    this.store.getConversations().then(() => {
      this.reselect(this.lastConversation);
      const el = this.$refs.list;
      const scrollToOffset = el.scrollHeight - 10;
      el.scrollTo({top: scrollToOffset, behavior: 'instant'});
      this.conversationsLoaded = true;
    });
  },
    isActive(conversation) {
      return conversation.status === 'Active';
    },
    formatDate: function(date) {
      if(new Date().getFullYear() === parseISO(date).getFullYear()) {
        return format(parseISO(date), 'M/d');
      }
      return format(parseISO(date), 'M/d/yyyy');
    },
    formatStatus: function (conversation) {
      if(this.isActive(conversation)) {
        return;
      }
      return this.formatDate(conversation.expires_at ?? conversation.feedback_event.end_date);
    },
    isSelected: function (id) {
      return false;
      // return Number(id) === Math.max(...this.store.conversations.map(object => Number(object.id)));
    },
    reselect: function (conversation){
      this.$emit('reselect', conversation);
    },
    async stopConversation(){
      await this.store.stopConversation(this.lastConversation.id).catch(() => {
        alert("An error occurred while stopping the conversation. Please try again.");
      });
      this.loadConversations();
    },
  },
}
</script>

<style scoped>
.list-group {
  max-height: 70vh;
}

.conversation-card {
  white-space: nowrap;
  position: relative;
}

/* Used conditionally on conversation-card */
.conversation-active {
  background-color: #bdf9bd;
}

.conversation-card .conversation-title > * {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* To maintain tab button functionality */
.conversation-card .jump-button {
  position: absolute;
  left: -10000px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
}
.conversation-card:hover .jump-button, .conversation-card .jump-button:focus {
  position: absolute;
  top: 0;
  left: auto;
  right: 0;
  width: auto;
  height: auto;
}

/* Used for methods.isSelected */
.selected-item {
  outline: 3px solid cornflowerblue;
  margin-right: 3px;
  margin-left: 3px;
}

.conversation-timeline {
  z-index: 1;
  padding-right: 0;
  padding-left: 0;
  max-width: max-content;
}

/* .timeline-focused: Used conditionally on root div */
.conversation-timeline:focus-within, .conversation-timeline:hover, .timeline-focused {
  position: absolute;
  /* If max-content is large, show about 55 characters, but cut off at 60% screen width */
  width: min(30em, 60vw);

  /* Ensure potentially hidden elements are shown */
  & .hide-when-narrow {
    visibility: visible;
  }
  & .remove-when-narrow {
    display: unset;
  }
}

@media (max-width: 55rem) {
  .hide-when-narrow {
    visibility: hidden;
  }
  .remove-when-narrow {
    display: none;
  }
}

.conversation-icon {
  right: .5em;
}
</style>
